import { useState, useEffect } from "react";
import { RiCloseLargeLine } from "react-icons/ri";
import { useAdmin } from "../../hook/useAdmin";

const StockNoticeModal = ({ isOpen: controlledIsOpen, onClose: controlledOnClose}) => {
  const { isAdmin, isLoading } = useAdmin();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [medicineStock, setMedicineStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  // Use dynamic system date
  const today = new Date();

  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setIsModalOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);
  // Check if modal was shown today
  useEffect(() => {
    // This only runs if the modal is NOT being controlled by a parent
    if (!isLoading && isAdmin && controlledIsOpen === undefined) {
      const lastShownDate = localStorage.getItem("stockNoticeModalLastShown");
      const todayDateString = today.toISOString().split("T")[0];
      if (lastShownDate !== todayDateString) {
        setIsModalOpen(true);
      }
    }
  }, [isAdmin, isLoading, controlledIsOpen]);

  // Fetch medicine purchase data from API
  useEffect(() => {
    if (!isModalOpen || !isAdmin) return;

    const fetchMedicineStock = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch("https://api.muktihospital.com/api/medicine-purchase", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
          },
        });

        const data = await response.json();
        // console.log("Raw API response:", data);
        // console.log("Response type:", typeof data);
        // console.log("Response keys:", Object.keys(data));

        if (response.ok) {
          let processedStock = [];
          
          // Case 1: Array of purchase records
          if (Array.isArray(data)) {
            processedStock = data
              .flatMap((purchase) =>
                purchase.items && Array.isArray(purchase.items)
                  ? purchase.items.map((item) => ({
                      medicineName: item.medicineName || "Unknown Medicine",
                      batchId: item.batchNo || "Unknown Batch",
                      expiryDate: item.expiryDate || "1970-01-01",
                      stock: item.availableQuantity !== undefined ? item.availableQuantity : item.availableQuantity || 0,
                    }))
                  : []
              )
              .filter((item) => item.medicineName && item.batchId && item.expiryDate);
          }
          // Case 2: Single object with items array
          else if (data.items && Array.isArray(data.items)) {
            processedStock = data.items.map((item) => ({
              medicineName: item.medicineName || "Unknown Medicine",
              batchId: item.batchNo || "Unknown Batch",
              expiryDate: item.expiryDate || "1970-01-01",
              stock: item.availableQuantity !== undefined ? item.availableQuantity : item.availableQuantity || 0,
            }));
          }
          // Case 3: Object with 'data' property
          else if (data.data && Array.isArray(data.data)) {
            processedStock = data.data
              .flatMap((purchase) =>
                purchase.items && Array.isArray(purchase.items)
                  ? purchase.items.map((item) => ({
                      medicineName: item.medicineName || "Unknown Medicine",
                      batchId: item.batchNo || "Unknown Batch",
                      expiryDate: item.expiryDate || "1970-01-01",
                      stock: item.availableQuantity !== undefined ? item.availableQuantity : item.availableQuantity || 0,
                    }))
                  : []
              )
              .filter((item) => item.medicineName && item.batchId && item.expiryDate);
          }
          // Case 4: Object with 'purchases' property
          else if (data.purchases && Array.isArray(data.purchases)) {
            processedStock = data.purchases
              .flatMap((purchase) =>
                purchase.items && Array.isArray(purchase.items)
                  ? purchase.items.map((item) => ({
                      medicineName: item.medicineName || "Unknown Medicine",
                      batchId: item.batchNo || "Unknown Batch",
                      expiryDate: item.expiryDate || "1970-01-01",
                      stock: item.availableQuantity !== undefined ? item.availableQuantity : item.availableQuantity || 0,
                    }))
                  : []
              )
              .filter((item) => item.medicineName && item.batchId && item.expiryDate);
          }
          // Case 5: Error message in response
          else if (data.error) {
            setError(data.error);
            return;
          }
          // Case 6: Response is a string
          else if (typeof data === "string") {
            setError(data);
            return;
          }
          else {
            setError("Unexpected API response format. Check console for details.");
            // console.log("Failed to process response. Expected array, object with 'items', 'data', or 'purchases' key, or error message.");
            return;
          }

          setMedicineStock(processedStock);
        } else {
          setError(`Failed to fetch medicine purchase data (Status: ${response.status})`);
        }
      } catch (err) {
        setError("Unable to connect to the server. Please try again later.");
        console.error("Fetch medicine purchase error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMedicineStock();
  }, [isModalOpen, isAdmin]);

  // Precompute expiry dates to avoid repeated new Date() calls
  const medicineStockWithDates = medicineStock.map((medicine) => ({
    ...medicine,
    expiryDateObj: new Date(medicine.expiryDate),
  }));

  // For expired medicines with stock > 0
  const expiredInStock = medicineStockWithDates.filter(
    (medicine) => medicine.expiryDateObj < today && medicine.stock > 0
  );

  // Filter for out-of-stock medicines with expiryDate > today
  const outOfStock = medicineStockWithDates.filter(
    (medicine) => medicine.expiryDateObj > today && medicine.stock <= 0
  );

  // Handle close button click and store the date in localStorage
 const handleClose = () => {
    setIsModalOpen(false);
    const todayDateString = today.toISOString().split("T")[0];
    localStorage.setItem("stockNoticeModalLastShown", todayDateString);

    if (controlledOnClose) {
      controlledOnClose();
    }
  };

  // Don't render the modal if it's not open or user is not an admin
  if (!isModalOpen || !isAdmin || isLoading) return null;

  return (
    <div className="fixed w-full h-screen overflow-hidden bg-black/50 top-0 left-0 z-50 flex justify-center items-center">
        <div className="max-w-[400px] w-full max-h-screen overflow-y-auto bg-white rounded-lg shadow-lg">
            <div className="w-full flex justify-end border-b border-gray-300 p-4">
            <button
              onClick={handleClose}
              className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
            >
              <RiCloseLargeLine />
            </button>
          </div>
         <div className="p-4">
             {loading ? (
            <div className="py-4 text-center">
              <p>Loading...</p>
            </div>
          ) : error ? (
            <div className="py-4 text-center text-red-500">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="py-4">
                <h2 className="text-center text-base">Date Expired Medicine</h2>
                <table className="mt-3 border-collapse w-full table-fixed max-h-screen overflow-y-auto">
                  <thead>
                    <tr>
                      <th className="text-center border border-M-text-color/20 text-xs px-3 py-1">
                        Medicine Name
                      </th>
                      <th className="text-center border border-M-text-color/20 text-xs px-3 py-1">
                        Batch ID
                      </th>
                      <th className="text-center border border-M-text-color/20 text-xs px-3 py-1">
                        Expiry Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiredInStock.map((medicine, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}
                      >
                        <td className="border border-gray-200 p-1 py-2 text-xs">
                          {medicine.medicineName}
                        </td>
                        <td className="border border-gray-200 p-1 py-2 text-xs text-center">
                          {medicine.batchId}
                        </td>
                        <td className="border border-gray-200 p-1 py-2 text-xs text-center text-red-500">
                          {medicine.expiryDate}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="py-4">
                <h2 className="text-center text-base">Stock Out Medicine</h2>
                <table className="mt-3 border-collapse w-full table-fixed max-h-screen overflow-y-auto">
                  <thead>
                    <tr>
                      <th className="text-center border border-M-text-color/20 text-xs px-3 py-1">
                        Medicine Name
                      </th>
                      <th className="text-center border border-M-text-color/20 text-xs px-3 py-1">
                        Batch ID
                      </th>
                      <th className="text-center border border-M-text-color/20 text-xs px-3 py-1">
                        Stock
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {outOfStock.map((medicine, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}
                      >
                        <td className="border border-gray-200 p-1 py-2 text-xs">
                          {medicine.medicineName}
                        </td>
                        <td className="border border-gray-200 p-1 py-2 text-xs text-center">
                          {medicine.batchId}
                        </td>
                        <td className="border border-gray-200 p-1 py-2 text-xs text-center text-red-500">
                          {medicine.stock}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
         </div>
          </div>
        </div>
  );
};

export default StockNoticeModal;